/**
 * Peças trocadas — linhas de detalhe e valores por coluna.
 */

import { FONTE_CHAMADOS_TECNICOS } from '../constants/relatoriosFontes';
import {
  COLUMNS_CONFIG_PECAS,
  PECAS_COLUMN_IDS,
  PECAS_MAIN_ROW_COLUMN_IDS,
  PECAS_DETAIL_ROW_COLUMN_IDS,
  PECAS_DETAIL_TRIGGER_COLUMN_IDS,
  PECAS_DETAIL_INLINE_FIELD_ORDER,
} from '../constants/relatoriosColumnsPecas';
import { columnRefKey } from './relatoriosColumnRefs';

export const PECAS_LINHAS_ROW_KEY = '__pecasLinhas';

export { PECAS_DETAIL_ROW_COLUMN_IDS } from '../constants/relatoriosColumnsPecas';

export function pecasColumnKey(columnId) {
  return columnRefKey(FONTE_CHAMADOS_TECNICOS, columnId);
}

export function isPecasColumnId(id) {
  return PECAS_COLUMN_IDS.includes(id);
}

function parseColumnIdFromKey(key) {
  if (!key || typeof key !== 'string') return key;
  const i = key.indexOf(':');
  return i > 0 ? key.slice(i + 1) : key;
}

function getSelectedPecasColumnIds(columnRefs) {
  return new Set(
    (columnRefs || [])
      .filter((r) => r.fonte === FONTE_CHAMADOS_TECNICOS && isPecasColumnId(r.id))
      .map((r) => r.id)
  );
}

/** Campos a–e selecionados (disparam sublinhas). */
export function getSelectedPecasDetailFieldIds(columnRefs) {
  const selected = getSelectedPecasColumnIds(columnRefs);
  return PECAS_DETAIL_TRIGGER_COLUMN_IDS.filter((id) => selected.has(id));
}

/** Campos visíveis na sublinha, na ordem a–g, conforme marcados. */
export function getPecasDetailInlineFieldIds(columnRefs) {
  if (getSelectedPecasDetailFieldIds(columnRefs).length === 0) return [];
  const selected = getSelectedPecasColumnIds(columnRefs);
  return PECAS_DETAIL_INLINE_FIELD_ORDER.filter((id) => selected.has(id));
}

export function hasPecasDetailRowsEnabled(columnRefs) {
  return getSelectedPecasDetailFieldIds(columnRefs).length > 0;
}

/** @deprecated use hasPecasDetailRowsEnabled(columnRefs) */
export function hasPecasDetailColumns(visibleColumnIds) {
  return (visibleColumnIds || []).some((key) =>
    PECAS_DETAIL_TRIGGER_COLUMN_IDS.includes(parseColumnIdFromKey(key))
  );
}

/** Remove colunas a–e do cabeçalho da tabela (só painel na sublinha). */
export function filterPecasTriggerKeysFromDisplay(keys) {
  return (keys || []).filter(
    (key) => !PECAS_DETAIL_TRIGGER_COLUMN_IDS.includes(parseColumnIdFromKey(key))
  );
}

export function estimatePecasDetailRowHeight() {
  return 22;
}

export function rowHasPecasDetailLines(row) {
  const lines = row?.[PECAS_LINHAS_ROW_KEY] || row?.__pecasLinhas;
  return Array.isArray(lines) && lines.length > 0;
}

export function sortPecasDetailLinesByQtdeDesc(lines) {
  if (!Array.isArray(lines) || lines.length <= 1) return lines || [];
  return [...lines].sort((a, b) => (Number(b?.qtde) || 0) - (Number(a?.qtde) || 0));
}

export function getPecasDetailFieldLabel(fieldId) {
  if (fieldId === 'pecaCustoUnitario') return 'Custo';
  if (fieldId === 'pecaOs') return 'OS';
  return COLUMNS_CONFIG_PECAS.find((c) => c.id === fieldId)?.label || fieldId;
}

export function hasAnyPecasColumns(visibleColumnIds) {
  return (visibleColumnIds || []).some((key) => {
    const id = key.includes(':') ? key.split(':').slice(1).join(':') : key;
    return isPecasColumnId(id);
  });
}

function formatMoney(val) {
  if (val == null || val === '' || Number.isNaN(Number(val))) return '-';
  const n = Number(val);
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatQty(val) {
  if (val == null || val === '' || Number.isNaN(Number(val))) return '0';
  const n = Number(val);
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

function formatInteger(val) {
  if (val == null || val === '' || Number.isNaN(Number(val))) return '-';
  return Math.trunc(Number(val)).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

function collectOsFromLine(line) {
  const set = new Set();
  const single = line?.os != null ? String(line.os).trim() : '';
  if (single) set.add(single);
  (line?.osList || []).forEach((os) => {
    const s = String(os ?? '').trim();
    if (s) set.add(s);
  });
  return set;
}

/** Números de OS da troca (vírgula se houver mais de uma). */
export function formatPecasOsList(pecasLine) {
  const set = collectOsFromLine(pecasLine);
  if (!set.size) return '-';
  return [...set]
    .sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }))
    .join(', ');
}

/** Agrupa detalhe por código + peça + referência + marca (qtde/custo somados). */
export function buildPecasDetailLines(rawLines) {
  const map = new Map();
  for (const line of rawLines || []) {
    const cod = String(line.cod ?? '').trim();
    const nome = String(line.peca ?? '').trim();
    const ref = String(line.referencia ?? '').trim();
    const marca = String(line.marca ?? '').trim();
    const key = `${cod}\x1f${nome}\x1f${ref}\x1f${marca}`;
    const qtde = Number(line.qtde) || 0;
    const custoTotal = Number(line.custoTotal) || 0;
    const custoUnit = Number(line.custoUnit) || 0;
    const lineOs = collectOsFromLine(line);
    const prev = map.get(key);
    if (!prev) {
      map.set(key, {
        cod,
        peca: nome,
        vidaUtil: line.vidaUtil,
        referencia: ref,
        marca,
        custoUnit,
        qtde,
        custoTotal,
        _unitCount: 1,
        _osSet: lineOs,
      });
      continue;
    }
    if (prev.vidaUtil == null && line.vidaUtil != null) prev.vidaUtil = line.vidaUtil;
    prev.qtde += qtde;
    prev.custoTotal += custoTotal;
    if (Math.abs(prev.custoUnit - custoUnit) > 0.001) prev._unitCount += 1;
    lineOs.forEach((os) => prev._osSet.add(os));
  }
  return [...map.values()]
    .map((r) => ({
      cod: r.cod || '-',
      peca: r.peca || '-',
      vidaUtil: r.vidaUtil,
      referencia: r.referencia || '-',
      marca: r.marca || '-',
      custoUnit: r._unitCount > 1 ? '-' : formatMoney(r.custoUnit),
      qtde: r.qtde,
      custoTotal: r.custoTotal,
      osList: [...(r._osSet || [])].sort((a, b) =>
        a.localeCompare(b, 'pt-BR', { numeric: true })
      ),
    }))
    .sort((a, b) =>
      `${a.cod}${a.peca}`.localeCompare(`${b.cod}${b.peca}`, 'pt-BR')
    );
}

export function getPecasMainRowValue(row, columnId) {
  if (!row) return '-';
  if (columnId === 'pecaQtde') {
    const v = row[pecasColumnKey('pecaQtde')];
    return v != null && v !== '' ? formatQty(v) : '0';
  }
  if (columnId === 'pecaCustoTotal') {
    const v = row[pecasColumnKey('pecaCustoTotal')];
    return v != null && v !== '' ? formatMoney(v) : formatMoney(0);
  }
  return '-';
}

export function getPecasDetailRowValue(pecasLine, columnId) {
  if (!pecasLine) return '';
  if (!PECAS_DETAIL_ROW_COLUMN_IDS.includes(columnId)) return '';
  if (columnId === 'pecaQtde') return formatQty(pecasLine.qtde);
  if (columnId === 'pecaCustoTotal') return formatMoney(pecasLine.custoTotal);
  if (columnId === 'pecaCustoUnitario') {
    return typeof pecasLine.custoUnit === 'string' ? pecasLine.custoUnit : formatMoney(pecasLine.custoUnit);
  }
  if (columnId === 'pecaCod') return pecasLine.cod || '-';
  if (columnId === 'pecaNome') return pecasLine.peca || '-';
  if (columnId === 'pecaVidaUtil') return formatInteger(pecasLine.vidaUtil);
  if (columnId === 'pecaReferencia') return pecasLine.referencia || '-';
  if (columnId === 'pecaMarca') return pecasLine.marca || '-';
  if (columnId === 'pecaOs') return formatPecasOsList(pecasLine);
  return '-';
}

export function isPecasMainOnlyColumn(columnId) {
  return PECAS_MAIN_ROW_COLUMN_IDS.includes(columnId);
}

/** Rótulo do cabeçalho na tabela para Qtde / Custo total. */
export function getPecasTableHeaderLabel(label) {
  const base = String(label ?? '').trim();
  return base ? `(peças trocadas) ${base}` : '(peças trocadas)';
}
